import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useLanguage } from "../contexts/LanguageContext";
import { useOrganizationScope } from "../contexts/OrganizationScopeContext";
import { 
  TrendingUp, TrendingDown, Users, FileText, AlertTriangle, Plane,
  Clock, CheckCircle, XCircle, AlertCircle, Calendar, Shield,
  ChevronDown, RefreshCw, Wrench, Send, ArrowRight
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { ComplianceStatusWidget } from "../components/ComplianceStatusWidget";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from "recharts";

// Color constants
const COLORS = {
  primary: "#0ea5e9",
  success: "#10b981",
  warning: "#f59e0b",
  danger: "#ef4444",
  slate: "#64748b",
  navy: "#0f172a"
};

const SAIL_COLORS = {
  "I": "#10b981",
  "II": "#22c55e", 
  "III": "#f59e0b",
  "IV": "#f97316",
  "V": "#ef4444",
  "VI": "#dc2626"
};

const STATUS_COLORS = {
  draft: "#94a3b8",
  in_progress: "#0ea5e9",
  ready: "#10b981",
  completed: "#6366f1",
  cancelled: "#ef4444"
};

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, trend, trendValue, color = "sky", subtitle }) => {
  const colorMap = {
    sky: "bg-sky-50 text-sky-600 dark:bg-sky-900/20 dark:text-sky-400",
    emerald: "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400",
    amber: "bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400",
    rose: "bg-rose-50 text-rose-600 dark:bg-rose-900/20 dark:text-rose-400",
    slate: "bg-slate-50 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
  };

  return (
    <Card className="card-hover">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-[var(--text-secondary)]">{title}</p>
            <p className="text-3xl font-bold text-[var(--text-primary)] mt-1">
              {value}
            </p>
            {subtitle && <p className="text-xs text-[var(--text-muted)] mt-1">{subtitle}</p>}
            {trend && (
              <div className="flex items-center gap-1 mt-2">
                {trend === "up" ? (
                  <TrendingUp className="h-4 w-4 text-emerald-500" />
                ) : (
                  <TrendingDown className="h-4 w-4 text-rose-500" />
                )}
                <span className={`text-xs font-medium ${trend === "up" ? "text-emerald-600" : "text-rose-600"}`}>
                  {trendValue}
                </span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-lg ${colorMap[color]}`}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// U-Space Readiness Gauge Component
const USpaceReadinessGauge = ({ score, level, levelLabel, checklist, t }) => {
  const getColor = () => {
    if (level === "high") return "#10b981";
    if (level === "medium") return "#f59e0b";
    return "#ef4444";
  };

  const circumference = 2 * Math.PI * 60;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  return (
    <div className="flex flex-col items-center">
      {/* Gauge */}
      <div className="relative mb-4">
        <svg width="150" height="150" className="-rotate-90">
          <circle
            cx="75"
            cy="75"
            r="60"
            fill="none"
            stroke="var(--border-color)"
            strokeWidth="10"
          />
          <circle
            cx="75"
            cy="75"
            r="60"
            fill="none"
            stroke={getColor()}
            strokeWidth="10"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-1000"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-4xl font-bold text-[var(--text-primary)]">
            {score}%
          </span>
        </div>
      </div>
      
      {/* Level Badge */}
      <Badge 
        className={`mb-4 ${
          level === "high" ? "bg-emerald-100 text-emerald-800" :
          level === "medium" ? "bg-amber-100 text-amber-800" :
          "bg-rose-100 text-rose-800"
        }`}
      >
        {levelLabel}
      </Badge>
      
      {/* Checklist */}
      <div className="w-full space-y-2">
        {checklist?.map((item, idx) => (
          <div key={`checklist-${item.label_en?.substring(0, 15).replace(/\s+/g, '-') || idx}`} className="flex items-center gap-2 text-sm">
            {item.passed ? (
              <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
            ) : (
              <XCircle className="h-4 w-4 text-rose-500 flex-shrink-0" />
            )}
            <span className="text-[var(--text-secondary)] flex-1">
              {t.language === "cs" ? item.label_cs : item.label_en}
            </span>
            <span className="text-xs text-[var(--text-muted)] font-mono">
              {item.detail}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Document Expiry Alert Item
const DocumentAlert = ({ doc, severity, t }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString(t.language === "cs" ? "cs-CZ" : "en-US");
    } catch {
      return dateStr;
    }
  };

  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg ${
      severity === "red" ? "bg-rose-50 dark:bg-rose-900/20" : "bg-amber-50 dark:bg-amber-900/20"
    }`}>
      <AlertCircle className={`h-5 w-5 flex-shrink-0 ${
        severity === "red" ? "text-rose-500" : "text-amber-500"
      }`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[var(--text-primary)] truncate">
          {doc.document_name}
        </p>
        <p className="text-xs text-[var(--text-muted)]">
          {doc.entity_type}: {doc.entity_name}
        </p>
      </div>
      <div className="text-right">
        <p className={`text-sm font-medium ${severity === "red" ? "text-rose-600" : "text-amber-600"}`}>
          {formatDate(doc.expiry_date)}
        </p>
      </div>
    </div>
  );
};

// Service Warning Item
const ServiceWarningItem = ({ aircraft, t }) => {
  return (
    <div className="flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
      <Wrench className="h-5 w-5 text-amber-500 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[var(--text-primary)]">
          {aircraft.model}
        </p>
        <p className="text-xs text-[var(--text-muted)]">
          {aircraft.registration}
        </p>
      </div>
      <div className="w-24">
        <Progress value={aircraft.progress} className="h-2" />
        <p className="text-xs text-amber-600 mt-1 text-right">
          {aircraft.flight_hours}/{aircraft.service_interval}h
        </p>
      </div>
    </div>
  );
};

export const Analytics = () => {
  const { t, language } = useLanguage();
  const {
    activeOrganizationId,
    clearScope,
    isPlatformAdmin,
    organizations,
    setActiveOrganization,
  } = useOrganizationScope();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const selectedOrg = activeOrganizationId || "all";
  const [dateRange, setDateRange] = useState("month");
  const [submissionStats, setSubmissionStats] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    try {
      setRefreshing(true);
      
      // Calculate date range
      const now = new Date();
      let startDate, endDate;
      
      if (dateRange === "week") {
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      } else if (dateRange === "month") {
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      } else if (dateRange === "quarter") {
        startDate = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
      } else {
        startDate = new Date(now.getFullYear(), 0, 1);
      }
      endDate = now;
      
      let url = `/analytics/dashboard?start_date=${startDate.toISOString()}&end_date=${endDate.toISOString()}`;
      if (selectedOrg !== "all") {
        url += `&organization_id=${selectedOrg}`;
      }
      
      const response = await api.get(url);
      setData(response.data);
    } catch (error) {
      // Silent fail - analytics continues with empty data
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [dateRange, selectedOrg]);

  const fetchSubmissionStats = useCallback(async () => {
    if (isPlatformAdmin && !activeOrganizationId) {
      setSubmissionStats(null);
      return;
    }

    try {
      const response = await api.get('/submissions/stats');
      setSubmissionStats(response.data);
    } catch (error) {
      // Silent fail - no submission warnings shown
    }
  }, [activeOrganizationId, isPlatformAdmin]);

  useEffect(() => {
    fetchAnalytics();
    fetchSubmissionStats();
  }, [fetchAnalytics, fetchSubmissionStats]);

  // Prepare chart data
  const statusChartData = data?.operational_overview?.status_breakdown 
    ? Object.entries(data.operational_overview.status_breakdown).map(([key, value]) => ({
        name: t.analytics?.status?.[key] || key,
        value,
        color: STATUS_COLORS[key]
      })).filter(item => item.value > 0)
    : [];

  const sailChartData = data?.safety_compliance?.sail_distribution
    ? Object.entries(data.safety_compliance.sail_distribution).map(([key, value]) => ({
        name: `SAIL ${key}`,
        value,
        fill: SAIL_COLORS[key]
      }))
    : [];

  const flightHoursData = data?.fleet_status?.flight_hours_chart || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[var(--text-secondary)]">{t.common?.loading || "Loading..."}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="analytics-dashboard">
      {/* Header - Management Dashboard Style with colored top border */}
      <div className="border-t-4 border-indigo-500 -mx-6 -mt-6 px-6 pt-6 bg-gradient-to-b from-indigo-50/50 to-transparent">
        <div className="flex items-center justify-between flex-wrap gap-4 pb-4">
          <div>
            <h1 className="text-3xl font-bold text-[var(--text-primary)]">
              {t.analytics?.title || "Analytics Dashboard"}
            </h1>
            <p className="text-[var(--text-secondary)] mt-1">
              {t.analytics?.subtitle || "Operational insights and U-Space readiness"}
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Date Range Filter */}
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-[140px]">
                <Calendar className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="week">{t.analytics?.week || "This Week"}</SelectItem>
                <SelectItem value="month">{t.analytics?.month || "This Month"}</SelectItem>
                <SelectItem value="quarter">{t.analytics?.quarter || "This Quarter"}</SelectItem>
                <SelectItem value="year">{t.analytics?.year || "This Year"}</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Organization Filter (Admin only) */}
          {isPlatformAdmin && organizations.length > 0 && (
            <Select
              value={selectedOrg}
              onValueChange={(value) => value === "all" ? clearScope() : setActiveOrganization(value)}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder={t.analytics?.allOrgs || "All Organizations"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t.analytics?.allOrgs || "All Organizations"}</SelectItem>
                {organizations.map(org => (
                  <SelectItem key={org.id} value={org.id}>{org.company_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          {/* Refresh Button */}
          <Button 
            variant="outline" 
            size="icon"
            onClick={fetchAnalytics}
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>
      </div>

      {/* UCL Submission Warnings - Alert Banner */}
      {submissionStats && (submissionStats.pending_supplement > 0 || submissionStats.expired > 0) && (
        <div 
          className="bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-lg p-4 shadow-sm"
          data-testid="submission-warnings-banner"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Send className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">
                    {language === "cs" ? "Upozornění na podání ÚCL" : "UCL Submission Alerts"}
                  </h3>
                  <p className="text-sm text-slate-600">
                    {language === "cs" ? "Vyžadována akce" : "Action required"}
                  </p>
                </div>
              </div>
              
              <div className="flex gap-6 ml-4">
                {submissionStats.pending_supplement > 0 && (
                  <div className="text-center px-4 py-1 bg-orange-100 rounded-lg">
                    <p className="text-2xl font-bold text-orange-600">{submissionStats.pending_supplement}</p>
                    <p className="text-xs text-orange-700">{language === "cs" ? "Čeká na doplnění" : "Pending Supplement"}</p>
                  </div>
                )}
                {submissionStats.expired > 0 && (
                  <div className="text-center px-4 py-1 bg-yellow-100 rounded-lg">
                    <p className="text-2xl font-bold text-yellow-600">{submissionStats.expired}</p>
                    <p className="text-xs text-yellow-700">{language === "cs" ? "Vypršelo" : "Expired"}</p>
                  </div>
                )}
              </div>
            </div>
            
            <Link
              to="/submissions"
              className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium text-sm transition-colors"
            >
              <Send className="h-4 w-4" />
              {language === "cs" ? "Zobrazit podání" : "View Submissions"}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      )}

      {/* Compliance Status Widget - Wave 3 Block 3C */}
      <ComplianceStatusWidget />

      {/* Section 1: Operational Overview */}
      <div className="pt-2">
        <div className="flex items-center gap-3 mb-4 pb-2 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">
            {t.analytics?.operationalOverview || "Operational Overview"}
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <StatCard
            title={t.analytics?.totalMissions || "Missions This Period"}
            value={data?.operational_overview?.total_missions || 0}
            icon={FileText}
            trend={data?.operational_overview?.missions_trend_direction}
            trendValue={`${data?.operational_overview?.missions_trend > 0 ? '+' : ''}${data?.operational_overview?.missions_trend}%`}
            color="sky"
          />
          <StatCard
            title={t.analytics?.avgTimeToReady || "Avg. Time to Ready"}
            value={`${data?.operational_overview?.avg_time_to_ready_hours || 0}h`}
            icon={Clock}
            color="emerald"
            subtitle={t.analytics?.fromCreation || "from creation"}
          />
          <StatCard
            title={t.analytics?.activePilots || "Active Pilots"}
            value={data?.operational_overview?.active_pilots || 0}
            icon={Users}
            color="slate"
            subtitle={t.analytics?.thisPeriod || "this period"}
          />
          <StatCard
            title={t.analytics?.incidentsThisMonth || "Incidents"}
            value={data?.safety_compliance?.incidents_this_month || 0}
            icon={AlertTriangle}
            color={data?.safety_compliance?.incidents_this_month > 0 ? "rose" : "slate"}
          />
        </div>
      </div>

      {/* Section 2: Charts - Larger and with section divider */}
      <div className="pt-4">
        <div className="flex items-center gap-3 mb-4 pb-2 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">
            {t.analytics?.missionAnalysis || "Mission Analysis"}
          </h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Mission Status Donut */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t.analytics?.missionStatusBreakdown || "Mission Status Breakdown"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <div className="min-w-[300px]">
                  {statusChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={statusChartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {statusChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[280px] flex items-center justify-center text-[var(--text-muted)]">
                      {t.common?.noData || "No data available"}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* SAIL Distribution */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t.analytics?.sailDistribution || "SAIL Level Distribution"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <div className="min-w-[300px]">
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={sailChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {sailChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Section 3: Safety & Compliance + U-Space */}
      <div className="pt-4">
        <div className="flex items-center gap-3 mb-4 pb-2 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">
            {t.analytics?.safetyAndUSpace || "Safety, Compliance & U-Space"}
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Compliance Metrics */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t.analytics?.safetyCompliance || "Safety & Compliance"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Compliance Rate */}
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-[var(--text-secondary)]">
                  {t.analytics?.complianceRate || "Compliance Rate"}
                </span>
                <span className="font-medium text-[var(--text-primary)]">
                  {data?.safety_compliance?.compliance_rate || 0}%
                </span>
              </div>
              <Progress value={data?.safety_compliance?.compliance_rate || 0} className="h-2" />
              <p className="text-xs text-[var(--text-muted)] mt-1">
                {t.analytics?.readyBeforeMission || "Missions ready before date"}
              </p>
            </div>
            
            {/* SORA Confidence Rate */}
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-[var(--text-secondary)]">
                  {t.analytics?.soraConfidence || "SORA Confidence Rate"}
                </span>
                <span className="font-medium text-[var(--text-primary)]">
                  {data?.safety_compliance?.sora_confidence_rate || 0}%
                </span>
              </div>
              <Progress value={data?.safety_compliance?.sora_confidence_rate || 0} className="h-2" />
            </div>
            
            {/* Top Missing Requirements */}
            <div className="pt-2">
              <p className="text-sm font-medium text-[var(--text-primary)] mb-2">
                {t.analytics?.topMissingReq || "Top Missing Requirements"}
              </p>
              {data?.safety_compliance?.top_missing_requirements?.length > 0 ? (
                <div className="space-y-2">
                  {data.safety_compliance.top_missing_requirements.map((req, idx) => (
                    <div key={`missing-req-${req.requirement?.substring(0, 20).replace(/\s+/g, '-') || idx}`} className="flex items-center gap-2 text-sm">
                      <span className="w-5 h-5 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center text-xs font-medium">
                        {req.count}
                      </span>
                      <span className="text-[var(--text-secondary)] truncate flex-1">
                        {req.requirement}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-[var(--text-muted)]">
                  {t.common?.none || "None"}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* U-Space Readiness Score */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t.analytics?.uSpaceReadiness || "U-Space Readiness"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <USpaceReadinessGauge
              score={data?.uspace_readiness?.score || 0}
              level={data?.uspace_readiness?.level || "low"}
              levelLabel={language === "cs" 
                ? data?.uspace_readiness?.level_label_cs 
                : data?.uspace_readiness?.level_label_en}
              checklist={data?.uspace_readiness?.checklist}
              t={{ language }}
            />
          </CardContent>
        </Card>

        {/* Fleet Status */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              <span>{t.analytics?.fleetStatus || "Fleet Status"}</span>
              <Badge variant="outline">{data?.fleet_status?.total_aircraft || 0} {t.analytics?.drones || "drones"}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Top Flight Hours Chart */}
            <div className="mb-4">
              <p className="text-sm text-[var(--text-secondary)] mb-2">
                {t.analytics?.topFlightHours || "Top Flight Hours"}
              </p>
              <ResponsiveContainer width="100%" height={120}>
                <BarChart data={flightHoursData} layout="vertical">
                  <XAxis type="number" hide />
                  <YAxis dataKey="model" type="category" width={100} tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="hours" fill={COLORS.primary} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            
            {/* Service Warnings */}
            <div>
              <p className="text-sm text-[var(--text-secondary)] mb-2">
                {t.analytics?.approachingService || "Approaching Service Interval"}
              </p>
              {data?.fleet_status?.approaching_service?.length > 0 ? (
                <div className="space-y-2">
                  {data.fleet_status.approaching_service.slice(0, 3).map((ac, idx) => (
                    <ServiceWarningItem key={`service-${ac.id || ac.registration || idx}`} aircraft={ac} t={t} />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-emerald-600 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  {t.analytics?.allDronesOk || "All drones within service limits"}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
        </div>
      </div>

      {/* Section 4: Document Expiry Alerts */}
      <div className="pt-4">
        <div className="flex items-center gap-3 mb-4 pb-2 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">
            {t.analytics?.documentExpiry || "Document Monitoring"}
          </h2>
        </div>
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              {t.analytics?.documentAlerts || "Document Expiry Alerts"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Expiring in 30 days */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Badge className="bg-rose-100 text-rose-800">
                    {data?.document_alerts?.count_30 || 0}
                  </Badge>
                  <span className="text-sm font-medium text-[var(--text-primary)]">
                    {t.analytics?.expiring30 || "Expiring in 30 days"}
                  </span>
                </div>
                {data?.document_alerts?.expiring_30_days?.length > 0 ? (
                  <div className="space-y-2">
                    {data.document_alerts.expiring_30_days.map((doc, idx) => (
                      <DocumentAlert key={`doc30-${doc.id || doc.document_name?.substring(0, 15) || idx}`} doc={doc} severity="red" t={{ language }} />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-[var(--text-muted)]">
                    {t.analytics?.noExpiringDocs || "No documents expiring soon"}
                  </p>
                )}
              </div>
              
              {/* Expiring in 31-90 days */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Badge className="bg-amber-100 text-amber-800">
                    {data?.document_alerts?.count_90 || 0}
                  </Badge>
                  <span className="text-sm font-medium text-[var(--text-primary)]">
                    {t.analytics?.expiring90 || "Expiring in 31-90 days"}
                  </span>
                </div>
                {data?.document_alerts?.expiring_31_90_days?.length > 0 ? (
                  <div className="space-y-2">
                    {data.document_alerts.expiring_31_90_days.map((doc, idx) => (
                      <DocumentAlert key={`doc90-${doc.id || doc.document_name?.substring(0, 15) || idx}`} doc={doc} severity="yellow" t={{ language }} />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-[var(--text-muted)]">
                    {t.analytics?.noExpiringDocs || "No documents expiring"}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Analytics;
